import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../AuthContext';
import useAssuranceSettled from '../auth/useAssuranceSettled';
import { getMyCompanyAccess } from '../../services/companyAccessService';
import { isOfficeLocked, isWarning } from '../../utils/company/companyAccessLevel';
import { writeLastKnownAccess } from '../../utils/company/lastKnownAccessLevel';
import { onCompanyLockRefusal } from '../../utils/company/officeLockDialogBus';
import { startCompanyAccessLive } from './companyAccessLive';
import {
  currentViewEpoch, peekViewSessionKey, readViewCache, resolveViewSessionKey, writeViewCache,
} from '../../utils/platform/platformViewCache';

export { isOfficeLocked, isWarning };

/**
 * KAN-200 Ship A — ONE store for the company's access level, read by EVERY
 * tenant role (dashboard warning row, read-only dialog gate, Plan & billing
 * card). Same shape as useCompanyPlan: stale-while-revalidate, memory-only
 * cache keyed `${userId}:${aal}` + tenant (cleared at sign-out through
 * platformViewCache), deduped reads, refetch on the realtime ping and on
 * resume/focus. A failed fetch keeps the last value; with none, the company is
 * treated as `full` — client UX only, the server is the boundary.
 */
const scopeOf = (tenantId) => `companyAccess:${tenantId}`;
export const COMPANY_ACCESS_CACHE_CAP = 3;
const MOUNT_MIN_INTERVAL_MS = 5000;

const subscribers = new Set(); // (tenantId, access|null) => void
const inflight = new Map(); // tenantId -> Promise
let lastFetchAt = 0;

/** One deduped read; writes the cache and tells every mounted reader. */
export function refreshCompanyAccess(tenantId) {
  if (!tenantId) return Promise.resolve(null);
  if (inflight.has(tenantId)) return inflight.get(tenantId);
  const run = (async () => {
    lastFetchAt = Date.now();
    const key = await resolveViewSessionKey();
    const epoch = currentViewEpoch();
    const access = await getMyCompanyAccess();
    if (access && key && epoch === currentViewEpoch()) {
      writeViewCache(key, scopeOf(tenantId), access, { cap: COMPANY_ACCESS_CACHE_CAP, prefix: 'companyAccess:' });
    }
    subscribers.forEach((fn) => fn(tenantId, access));
    return access;
  })().finally(() => { inflight.delete(tenantId); });
  inflight.set(tenantId, run);
  return run;
}

const seedFor = (tenantId) => {
  const access = tenantId ? readViewCache(peekViewSessionKey(), scopeOf(tenantId)) : undefined;
  return access ? { access, status: 'ready' } : { access: null, status: 'loading' };
};

/**
 * @returns {{ access: object|null, level: 'full'|'grace'|'read_only'|'suspended',
 *   locked: boolean, warning: boolean, status: string, reload: () => Promise<object|null> }}
 */
export default function useCompanyAccess() {
  const { tenantId, sessionUser } = useAuth();
  const { settled } = useAssuranceSettled(sessionUser?.id);
  // Waits for the 2FA step: an aal1 read is empty under RLS.
  const enabled = Boolean(tenantId && sessionUser?.id && settled);
  const [state, setState] = useState(() => seedFor(tenantId));
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;

  const stateTenantRef = useRef(tenantId);
  useEffect(() => {
    // A different company: never carry the previous one's level (cache or not).
    const switched = stateTenantRef.current !== tenantId;
    stateTenantRef.current = tenantId;
    if (switched) setState(seedFor(tenantId));
    if (!enabled) return undefined;
    const cached = seedFor(tenantId);
    setState((s) => (!switched && s.access && cached.status === 'loading' ? s : cached));
    const onUpdate = (forTenant, access) => {
      if (forTenant !== tenantRef.current) return;
      setState((s) => (access ? { access, status: 'ready' } : { access: s.access, status: s.access ? 'ready' : 'error' }));
    };
    subscribers.add(onUpdate);
    // Many readers mount (every guarded button): a fresh answer is not re-read on each mount.
    if (!cached.access || Date.now() - lastFetchAt > MOUNT_MIN_INTERVAL_MS) void refreshCompanyAccess(tenantId);
    const stopLive = startCompanyAccessLive(tenantId, () => { void refreshCompanyAccess(tenantId); });
    // Resume backup: companyAccessLive re-runs every reader once per resume (KAN-1044).
    const stopRefusal = onCompanyLockRefusal(() => { void refreshCompanyAccess(tenantId); });
    return () => {
      subscribers.delete(onUpdate);
      stopLive();
      stopRefusal();
    };
  }, [enabled, tenantId]);

  // Returns the read's promise so callers (the billing self-heal) can wait for the new level.
  const reload = useCallback(() => refreshCompanyAccess(tenantRef.current), []);

  // The render where the tenant just changed (before the effect re-seeds): never show the old company's level.
  const staleTenant = stateTenantRef.current !== tenantId;
  const access = staleTenant ? null : state.access;
  const status = staleTenant ? 'loading' : state.status;
  const level = access?.level || 'full';
  // Cold-start memory (SuspendedAccessGate): remember the last resolved level for this person + company.
  const resolvedLevel = access?.level;
  const resolvedReason = access?.reason;
  useEffect(() => {
    if (resolvedLevel && sessionUser?.id && tenantId) writeLastKnownAccess(sessionUser.id, tenantId, { level: resolvedLevel, reason: resolvedReason });
  }, [resolvedLevel, resolvedReason, sessionUser?.id, tenantId]);
  return {
    tenantId, access, level, locked: isOfficeLocked(level), warning: isWarning(level), status, reload,
  };
}
